import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RoastData, RoastProfileRow, RoastStageState } from './types';
import type { Recipe } from '../../../types/recipe';
import { inventoryService } from '../../../services/inventoryService';
import { machineService } from '../../../services/machineService';
import { machineTypeService } from '../../../services/machineTypeService';
import type { InventoryItem } from '../../../types/inventory';
import type { AutocompleteOption } from '../../../components/common/AutocompleteSelect';
import { useWeightUnit } from '../../../hooks/useWeightUnit';
import type { RoastStage } from './StopWatch';
import { publishStopwatchTick, resetStopwatchTick } from './stopwatchTick';
import { useLanguage } from '../../../features/language/useLanguage';

interface LotRow {
  lotId: string;
  remaining: number;
  usedWeight: number | undefined;
}

interface UseRoastFormStateParams {
  mode: 'roast' | 'recipe';
  initialData?: Recipe;
}

export const useRoastFormState = ({ mode, initialData }: UseRoastFormStateParams) => {
  const { t } = useLanguage();
  const { unit, setUnit } = useWeightUnit();
  const today = new Date().toISOString().split('T')[0];

  const [date, setDate] = useState(today);
  const [roaster, setRoaster] = useState('');
  const [machine, setMachine] = useState(initialData?.machine || '');
  const [machineTypeId, setMachineTypeId] = useState(initialData?.machineTypeId || '');
  const [recipeName, setRecipeName] = useState(initialData?.name || '');
  const [notes, setNotes] = useState(initialData?.internalNotes || '');
  const [roastType, setRoastType] = useState((initialData?.roastType || '').toLowerCase());

  const [time, setTime] = useState(() => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  });

  const [beans, setBeans] = useState<InventoryItem[]>([]);
  const [selectedBean, setSelectedBean] = useState(initialData?.itemId || '');
  const [machineOptions, setMachineOptions] = useState<AutocompleteOption[]>([]);
  const [lotOptions, setLotOptions] = useState<
    { id: string; name: string; remaining: number; density?: number | null; moisture?: number | null }[]
  >([]);

  useEffect(() => {
    const loadBeans = async () => {
      try {
        const items = await inventoryService.getAll();
        const greenBeans = items.filter(item => item.itemType === 'green_bean');
        setBeans(greenBeans);
      } catch (error) {
        console.error('Failed to load beans', error);
      }
    };
    loadBeans();
  }, [t]);

  useEffect(() => {
    const loadMachineTypes = async () => {
      try {
        const activeTypes = await machineTypeService.getActive();
        const shouldLoadMachines = activeTypes.some((mt) => !mt.machineName?.trim());
        const machineNameById = shouldLoadMachines
          ? (await machineService.getAll()).reduce((acc, m) => {
              acc[m.id] = m.name;
              return acc;
            }, {} as Record<string, string>)
          : {};

        const nextOptions = activeTypes
          .map((mt) => {
            const machineName = mt.machineName?.trim() || machineNameById[mt.machineId] || '';
            const text = machineName ? `${machineName}${mt.name ? ' - ' : ''}${mt.name}` : mt.name;

            const min = typeof mt.minimumInputKg === 'number' ? mt.minimumInputKg : undefined;
            const max = typeof mt.maximumInputKg === 'number' ? mt.maximumInputKg : undefined;
            const limits =
              min !== undefined || max !== undefined
                ? ` (${t('workOrders.form.minInput')}: ${min ?? '-'}${t('inventory.history.unitKg')}, ${t('workOrders.form.maxInput')}: ${max ?? '-'}${t('inventory.history.unitKg')})`
                : '';

            return {
              id: mt.id,
              value: mt.id,
              text,
              label: `${text}${limits}`,
            };
          })
          .sort((a, b) => a.text.localeCompare(b.text));

        setMachineOptions(nextOptions);
      } catch (error) {
        console.error('Failed to load machine types', error);
        setMachineOptions([]);
      }
    };
    loadMachineTypes();
  }, [t]);

  const handleMachineChange = useCallback(
    (value: string, option?: AutocompleteOption) => {
      if (!value) {
        setMachine('');
        setMachineTypeId('');
        return;
      }

      if (option) {
        setMachine(option.text);
        setMachineTypeId(option.id || option.value || '');
        return;
      }

      const matchedOption = machineOptions.find(
        (item) => item.id === value || item.value === value || item.text === value,
      );

      if (matchedOption) {
        setMachine(matchedOption.text);
        setMachineTypeId(matchedOption.id || matchedOption.value || '');
        return;
      }

      setMachine(value);
      setMachineTypeId('');
    },
    [machineOptions],
  );

  const resolvedMachine = useMemo(() => {
    if (!machineTypeId || machineOptions.length === 0) {
      return machine;
    }

    const selectedOption = machineOptions.find(
      (item) => item.id === machineTypeId || item.value === machineTypeId,
    );
    return selectedOption?.text || machine;
  }, [machineTypeId, machineOptions, machine]);

  useEffect(() => {
    if (!selectedBean) {
      setTimeout(() => setLotOptions([]), 0);
      return;
    }
    let stale = false;
    const loadLots = async () => {
      try {
        const lots = await inventoryService.getLots(selectedBean);
        if (stale) return;
        const currentBean = beans.find(b => b.id === selectedBean);
        const profiles = currentBean?.greenBeanProfiles || [];

        setLotOptions(
          lots.map(l => {
            const lotProfile = profiles.find(p => p.lot_id === l.id && p.type === 'LotType');

            return {
              id: l.id,
              name: `${l.lotNumber} (${t('reports.details.qty')}: ${l.remainingQuantity}${t('inventory.history.unitKg')})`,
              remaining: l.remainingQuantity,
              density: lotProfile?.density,
              moisture: lotProfile?.moisture_content,
            };
          })
        );
      } catch (error) {
        if (!stale) console.error('Failed to load lots', error);
      }
    };
    loadLots();
    return () => { stale = true; };
  }, [selectedBean, beans, t]);

  const [lots, setLots] = useState<LotRow[]>([{ lotId: '', remaining: 0, usedWeight: undefined }]);

  const handleBeanChange = useCallback((beanId: string) => {
    setSelectedBean(beanId);
    setLots([{ lotId: '', remaining: 0, usedWeight: undefined }]);
  }, []);

  const handleLotChange = useCallback((index: number, lotId: string) => {
    const selected = lotOptions.find(l => l.id === lotId);
    const updated = [...lots];
    updated[index] = {
      ...updated[index],
      lotId,
      remaining: selected?.remaining || 0,
      usedWeight: undefined,
    };
    setLots(updated);
  }, [lotOptions, lots]);

  const handleWeightChange = useCallback((index: number, value: number | undefined) => {
    const updated = [...lots];
    updated[index].usedWeight = value;
    setLots(updated);
  }, [lots]);

  const addLotRow = useCallback(() => {
    setLots([...lots, { lotId: '', remaining: 0, usedWeight: undefined }]);
  }, [lots]);

  const removeLotRow = useCallback((index: number) => {
    setLots(lots.filter((_, i) => i !== index));
  }, [lots]);

  const totalInputWeight = useMemo(() => {
    return lots.reduce((sum, lot) => {
      const val = lot.usedWeight;
      return sum + (val === undefined || isNaN(val) ? 0 : val);
    }, 0);
  }, [lots]);

  const [roastData, setRoastData] = useState<
    Omit<RoastData, 'weightInput' | 'weightOutput'> & {
      weightInput: number | undefined;
      weightOutput: number | undefined;
      stageStates: RoastStageState[];
    }
  >(() => {
    const defaultStages: RoastStageState[] = [
      { key: 'start', label: t('roast.profile.stages.start'), time: '00:00', temp: '' },
      { key: 'turning_point', label: t('roast.profile.stages.turningPoint'), time: '', temp: '' },
      { key: 'yellow', label: t('roast.profile.stages.yellow'), time: '', temp: '' },
      { key: 'first_crack', label: t('roast.profile.stages.firstCrack'), time: '', temp: '' },
      { key: 'end', label: t('roast.profile.stages.end'), time: '', temp: '' },
    ];

    let stages = defaultStages;
    if (initialData?.stageSummary) {
      stages = defaultStages.map(s => {
        const entry = initialData.stageSummary?.[s.key];
        const rawTemp = entry?.temp;
        const numericTemp = typeof rawTemp === 'string' ? Number(rawTemp) : rawTemp;
        const safeTemp = numericTemp === undefined || Number.isNaN(numericTemp) ? s.temp : numericTemp;

        return {
          ...s,
          time: entry?.time || s.time,
          temp: safeTemp,
        };
      });
    }

    return {
      stages: [],
      weightInput: initialData?.targetBeanWeight || undefined,
      weightOutput: initialData?.targetWeightOutput || undefined,
      agtron: initialData?.agtron
        ? {
            wholeBean: typeof initialData.agtron.wholeBean === 'number' ? String(initialData.agtron.wholeBean) : '',
            groundBean: typeof initialData.agtron.groundBean === 'number' ? String(initialData.agtron.groundBean) : '',
            classification: {
              scaa: initialData.agtron.classification?.scaa || '',
              common: initialData.agtron.classification?.common || '',
            },
          }
        : { wholeBean: '', groundBean: '', classification: { scaa: '', common: '' } },
      profile: initialData?.profile || [],
      stageStates: stages,
    };
  });

  useEffect(() => {
    if (mode === 'roast') {
      setTimeout(() => setRoastData(prev => ({ ...prev, weightInput: totalInputWeight })), 0);
    }
  }, [totalInputWeight, mode]);

  const handleStageDataChange = useCallback((index: number, field: keyof RoastStageState, value: string | number) => {
    setRoastData(prev => {
      const newStages = [...prev.stageStates];
      newStages[index] = { ...newStages[index], [field]: value };
      return { ...prev, stageStates: newStages };
    });
  }, []);

  const handleWeightInputChange = useCallback((val: number | undefined) => {
    setRoastData(prev => ({ ...prev, weightInput: val }));
  }, []);

  const handleWeightOutputChange = useCallback((val: number | undefined) => {
    setRoastData(prev => ({ ...prev, weightOutput: val }));
  }, []);

  const updateStageTime = useCallback((key: string, timeStr: string, temp?: number) => {
    setRoastData(prev => ({
      ...prev,
      stageStates: prev.stageStates.map(s =>
        s.key === key ? { ...s, time: timeStr, ...(temp === undefined ? {} : { temp }) } : s
      ),
    }));
  }, []);

  const [stage, setStage] = useState<RoastStage>('idle');
  const [seconds, setSeconds] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const startTimeRef = useRef<number | null>(null);
  const secondsRef = useRef(0);

  useEffect(() => {
    if (!isRunning) return;

    let rafId: number;
    let lastTimestamp: number | null = null;
    // Only mirror the ref to React state when crossing a 30s bucket. The live
    // display and bucket-driven notifications read from the tick store, so
    // there is no need to re-render every frame just to advance the timer.
    let lastPublishedBucket = Math.floor(secondsRef.current / 30);

    const update = (timestamp: number) => {
      if (lastTimestamp !== null) {
        const delta = (timestamp - lastTimestamp) / 1000;
        secondsRef.current += delta;
        publishStopwatchTick(secondsRef.current);

        const currentBucket = Math.floor(secondsRef.current / 30);
        if (currentBucket > lastPublishedBucket) {
          lastPublishedBucket = currentBucket;
          setSeconds(secondsRef.current);
        }
      }
      lastTimestamp = timestamp;
      rafId = requestAnimationFrame(update);
    };

    rafId = requestAnimationFrame(update);
    return () => cancelAnimationFrame(rafId);
  }, [isRunning]);

  const formatTime = (totalSeconds: number) => {
    const m = Math.floor(totalSeconds / 60);
    const s = Math.floor(totalSeconds % 60);
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const handleMainButton = useCallback((atSeconds: number, beanTemp: number) => {
    const currentTime = formatTime(atSeconds);

    if (stage === 'idle') {
      setStage('turning');
      setIsRunning(true);
      updateStageTime('start', '00:00', beanTemp);
    } else if (stage === 'turning') {
      setStage('yellow');
      updateStageTime('turning_point', currentTime, beanTemp);
    } else if (stage === 'yellow') {
      setStage('firstCrack');
      updateStageTime('yellow', currentTime, beanTemp);
    } else if (stage === 'firstCrack') {
      setStage('endReady');
      updateStageTime('first_crack', currentTime, beanTemp);
    } else if (stage === 'endReady') {
      setStage('ended');
      setIsRunning(false);
      updateStageTime('end', currentTime, beanTemp);
    }
  }, [stage, updateStageTime]);

  const handleStartRunning = useCallback(() => {
    setIsRunning(true);
  }, []);

  const handlePauseRunning = useCallback(() => {
    setIsRunning(false);
  }, []);

  const handleRestart = useCallback(() => {
    startTimeRef.current = null;
    secondsRef.current = 0;
    setSeconds(0);
    resetStopwatchTick();
    setStage('idle');
    setIsRunning(false);

    setRoastData(prev => ({
      ...prev,
      stageStates: prev.stageStates.map(s => ({
        ...s,
        time: s.key === 'start' ? '00:00' : '',
        temp: '',
      })),
    }));
  }, []);

  const handleProfileChange = useCallback((profile: RoastProfileRow[]) => {
    setRoastData(prev => ({ ...prev, profile }));
  }, []);

  const handleAgtronChange = useCallback((field: 'whole' | 'ground', val: string) => {
    setRoastData(prev => ({
      ...prev,
      agtron: { ...prev.agtron, [field === 'whole' ? 'wholeBean' : 'groundBean']: val },
    }));
  }, []);

  return useMemo(() => ({
    unit,
    setUnit,
    date,
    setDate,
    time,
    setTime,
    roaster,
    setRoaster,
    machine: resolvedMachine,
    machineTypeId,
    handleMachineChange,
    recipeName,
    setRecipeName,
    notes,
    setNotes,
    roastType,
    setRoastType,
    beans,
    selectedBean,
    machineOptions,
    lotOptions,
    lots,
    totalInputWeight,
    roastData,
    setRoastData,
    handleBeanChange,
    handleLotChange,
    handleWeightChange,
    addLotRow,
    removeLotRow,
    handleStageDataChange,
    handleWeightInputChange,
    handleWeightOutputChange,
    stage,
    seconds,
    handleMainButton,
    handleStartRunning,
    handlePauseRunning,
    handleRestart,
    handleProfileChange,
    handleAgtronChange,
  }), [
    unit, setUnit, date, roaster, resolvedMachine, machineTypeId, recipeName, notes, roastType, time, beans,
    selectedBean, machineOptions, lotOptions, lots, totalInputWeight, roastData,
    stage, seconds, handleBeanChange, handleLotChange, handleWeightChange,
    addLotRow, removeLotRow, handleStageDataChange, handleWeightInputChange,
    handleWeightOutputChange, handleMainButton, handleStartRunning,
    handlePauseRunning, handleRestart, handleProfileChange, handleAgtronChange, handleMachineChange
  ]);
};
